import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, setLanguage, type Language } from '../i18n';
import { Wordmark } from './Wordmark';

/**
 * Guscio delle schermate di accesso, ricalcato sulla login dell'Allegato A:
 * stesse classi (`login-screen`, `login-box`, `login-card`...), quindi stesso
 * fondo navy, stessa griglia, stesso vetro smerigliato della card.
 */
interface AuthScreenProps {
  subtitle: string;
  children: ReactNode;
}

export function AuthScreen({ subtitle, children }: AuthScreenProps): ReactNode {
  const { t, i18n } = useTranslation();
  const current: Language = i18n.language.startsWith('en') ? 'en' : 'it';

  return (
    <div className="login-screen">
      <div className="login-box">
        <div className="login-wordmark">
          <Wordmark height={48} center />
        </div>
        <div className="login-subtitle">{subtitle}</div>

        <div className="login-card">{children}</div>

        <div className="login-lang">
          {SUPPORTED_LANGUAGES.map((language, index) => (
            <span key={language} style={{ display: 'contents' }}>
              {index > 0 ? <span className="login-lang-sep" /> : null}
              <button
                type="button"
                className="login-lang-btn"
                aria-pressed={language === current}
                onClick={() => void setLanguage(language)}
              >
                {t(`language.${language}`)}
              </button>
            </span>
          ))}
        </div>

        <div className="login-footer">{t('auth.footer')}</div>
      </div>
    </div>
  );
}

/** Messaggio di errore: assente nel riferimento, la cui login è finta. */
export function AuthError({ message }: { message: string }): ReactNode {
  return (
    <p className="login-error" role="alert">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6" strokeLinecap="round" />
        <path d="M12 16.5v.01" strokeLinecap="round" />
      </svg>
      {message}
    </p>
  );
}
